import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Package, X, Save, ExternalLink } from 'lucide-react';
import { 
  fetchItemKitById, 
  fetchItemsByItemKit, 
  createItem, 
  updateItem, 
  deleteItem,
  fetchRetailersByCountry,
  fetchCountriesForDropdown
} from '../services/api.ts';
import './ViewItemKit.css';

interface Item {
  id: number;
  id_itemkits: number;
  id_retailers: number;
  name: string;
  avater: string;
  price: number;
  regular_price: number;
  is_available: boolean;
  url: string;
  isbn: string;
  enable: boolean;
  retailer: {
    id: number | null;
    name: string | null;
    short_name: string | null;
    logo: string | null;
  };
}

interface ItemKit {
  id: number;
  name: string;
  avater: string;
  lowest_price: number;
  is_feature: boolean;
  enable: boolean;
  country_id: number;
}

interface Retailer {
  id: number;
  name: string;
  short_name: string;
  logo: string;
  enable: boolean;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

interface ItemFormData {
  id_itemkits: number;
  id_retailers: number;
  name: string;
  avater: string;
  price: number;
  regular_price: number;
  url: string;
  isbn: string;
  enable: boolean;
  country_id: number;
}

const ViewItemKit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [itemKit, setItemKit] = useState<ItemKit | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ItemFormData>({
    id_itemkits: 0,
    id_retailers: 0,
    name: '',
    avater: '',
    price: 0,
    regular_price: 0,
    url: '',
    isbn: '',
    enable: true,
    country_id: 0
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      // First fetch item kit to get its country_id
      const kitData = await fetchItemKitById(id);
      setItemKit(kitData);
      
      // Then fetch items and retailers filtered by the item kit's country
      const [itemsData, retailersData, countriesData] = await Promise.all([
        fetchItemsByItemKit(parseInt(id)),
        fetchRetailersByCountry(kitData.country_id),
        fetchCountriesForDropdown()
      ]);
      setItems(itemsData);
      setRetailers(retailersData);
      setCountries(countriesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    if (!itemKit) return;
    setFormData({
      id_itemkits: itemKit.id,
      id_retailers: retailers.length > 0 ? retailers[0].id : 0,
      name: '',
      avater: '',
      price: 0,
      regular_price: 0,
      url: '',
      isbn: '',
      enable: true,
      country_id: itemKit.country_id
    });
    setModalMode('add');
    setEditingItemId(null);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (item: Item) => {
    setFormData({
      id_itemkits: item.id_itemkits,
      id_retailers: item.id_retailers,
      name: item.name,
      avater: item.avater,
      price: item.price,
      regular_price: item.regular_price || 0,
      url: item.url,
      isbn: item.isbn,
      enable: item.enable,
      country_id: itemKit?.country_id || 0
    });
    setModalMode('edit');
    setEditingItemId(item.id);
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItemId(null);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setFormError(null);
      
      if (modalMode === 'add') {
        await createItem(formData);
      } else if (editingItemId) {
        await updateItem(editingItemId.toString(), {
          name: formData.name,
          avater: formData.avater,
          price: formData.price,
          regular_price: formData.regular_price,
          url: formData.url,
          enable: formData.enable
        });
      }
      
      closeModal();
      await loadData(); // Reload items
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId: number) => {
    try {
      await deleteItem(itemId);
      setDeleteConfirm(null);
      await loadData(); // Reload items
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete item');
    }
  };

  if (loading) {
    return (
      <div className="view-item-kit">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading item kit...</p>
        </div>
      </div>
    );
  }

  if (error || !itemKit) {
    return (
      <div className="view-item-kit">
        <div className="error">Error: {error || 'Item kit not found'}</div>
      </div>
    );
  }

  return (
    <div className="view-item-kit">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/item-kits')}>
          <ArrowLeft size={20} />
          Back to Item Kits
        </button>
        <div className="kit-header">
          <div className="kit-info">
            {itemKit.avater ? (
              <img src={itemKit.avater} alt={itemKit.name} className="kit-image" />
            ) : (
              <div className="kit-placeholder">
                <Package size={32} />
              </div>
            )}
            <div className="kit-details">
              <h2>{itemKit.name}</h2>
              <p className="kit-meta">
                <span className={`status-badge ${itemKit.enable ? 'active' : 'inactive'}`}>
                  {itemKit.enable ? 'Active' : 'Inactive'}
                </span>
                {itemKit.is_feature && <span className="feature-badge">Featured</span>}
                <span className="price-badge">${itemKit.lowest_price.toFixed(2)}</span>
              </p>
            </div>
          </div>
          <button className="add-btn" onClick={openAddModal}>
            <Plus size={18} />
            Add Item
          </button>
        </div>
      </div>

      <div className="items-section">
        <h3>Items ({items.length})</h3>
        
        {items.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <p>No items in this kit yet.</p>
            <button className="add-btn" onClick={openAddModal}>
              <Plus size={18} />
              Add First Item
            </button>
          </div>
        ) : (
          <div className="items-table-container">
            <table className="items-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Retailer</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="id-cell">{item.id}</td>
                    <td className="image-cell">
                      {item.avater ? (
                        <img src={item.avater} alt={item.name} className="item-thumbnail" />
                      ) : (
                        <div className="item-placeholder">
                          <Package size={20} />
                        </div>
                      )}
                    </td>
                    <td className="name-cell">
                      <span className="item-name">{item.name}</span>
                      {item.isbn && <span className="item-isbn">ISBN: {item.isbn}</span>}
                    </td>
                    <td className="retailer-cell">
                      {item.retailer?.logo && (
                        <img src={item.retailer.logo} alt={item.retailer.name || ''} className="retailer-logo" />
                      )}
                      <span>{item.retailer?.name || 'N/A'}</span>
                    </td>
                    <td className="price-cell">
                      <span className="price-current">${item.price.toFixed(2)}</span>
                      {item.regular_price > 0 && item.regular_price !== item.price && (
                        <span className="price-regular">${item.regular_price.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${item.enable ? 'active' : 'inactive'}`}>
                        {item.enable ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn url"
                          title="Open URL"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                      <button 
                        className="action-btn edit" 
                        onClick={() => openEditModal(item)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {deleteConfirm === item.id ? (
                        <div className="delete-confirm">
                          <button 
                            className="action-btn confirm-yes" 
                            onClick={() => handleDelete(item.id)}
                            title="Confirm Delete"
                          >
                            Yes
                          </button>
                          <button 
                            className="action-btn confirm-no" 
                            onClick={() => setDeleteConfirm(null)}
                            title="Cancel"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="action-btn delete" 
                          onClick={() => setDeleteConfirm(item.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Add New Item' : 'Edit Item'}</h3>
              <button className="close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            {formError && (
              <div className="form-error">
                <span>⚠️ {formError}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter item name"
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Retailer *</label>
                  <select
                    value={formData.id_retailers}
                    onChange={(e) => setFormData({ ...formData, id_retailers: parseInt(e.target.value) })}
                    required
                  >
                    <option value={0} disabled>Select a retailer</option>
                    {retailers.map((retailer) => (
                      <option key={retailer.id} value={retailer.id}>
                        {retailer.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Regular Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.regular_price}
                    onChange={(e) => setFormData({ ...formData, regular_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Image URL</label>
                  <input
                    type="text"
                    value={formData.avater}
                    onChange={(e) => setFormData({ ...formData, avater: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Product URL</label>
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://retailer.com/product"
                  />
                </div>
                
                <div className="form-group">
                  <label>ISBN/Barcode</label>
                  <input
                    type="text"
                    value={formData.isbn}
                    onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                    placeholder="Enter ISBN or barcode"
                  />
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <div className="toggle-wrapper">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={formData.enable}
                        onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">{formData.enable ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  <Save size={18} />
                  {saving ? 'Saving...' : (modalMode === 'add' ? 'Add Item' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewItemKit;
